/**
 * SatelliteLayer - Real-time satellite tracking on the theater map.
 * Uses satellite.js (SGP4) to propagate TLE orbits in real-time.
 * Shows satellite markers, ground tracks, and pass predictions.
 */
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import * as satellite from 'satellite.js';

// ============ SATELLITE TYPE COLORS ============
const TYPE_COLORS = {
  'ISR/IMINT': '#ff4444',
  'ISR/SIGINT': '#ff8800',
  'ISR/SAR': '#ffaa00',
  'ISR/EO': '#ddaa00',
  'ISR/Inspection': '#ff2266',
  'Alerte Missile': '#ff0066',
  'NAV/MIL': '#4488ff',
  'COMSAT/MIL': '#44aaff',
  'EO/Civil': '#44cc88',
};

const FLAG_URLS = {};
function getFlagUrl(code) {
  if (!code) return '';
  return `https://flagcdn.com/w40/${code}.png`;
}

// ============ SATELLITE ICON FACTORY ============
function createSatIcon(sat, color) {
  const flagUrl = getFlagUrl(sat.flag);
  const flagImg = flagUrl ? `<img src="${flagUrl}" style="width:16px;height:11px;border-radius:1px;vertical-align:middle;margin-right:3px;border:1px solid rgba(255,255,255,0.3);" />` : '';

  return L.divIcon({
    className: 'sat-marker',
    html: `
      <div class="sat-icon" style="--sat-color: ${color}">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="10" width="8" height="4" rx="1" fill="${color}" opacity="0.7"/>
          <rect x="15" y="10" width="8" height="4" rx="1" fill="${color}" opacity="0.7"/>
          <rect x="9" y="7" width="6" height="10" rx="2" fill="${color}"/>
          <circle cx="12" cy="12" r="3" fill="#fff" opacity="0.9"/>
          <circle cx="12" cy="12" r="1.5" fill="${color}"/>
        </svg>
        <div class="sat-pulse" style="border-color: ${color}"></div>
      </div>
      <div class="sat-label">${flagImg}<span style="color:${color}">${sat.name.length > 20 ? sat.name.split(' ').slice(0,2).join(' ') : sat.name}</span></div>
    `,
    iconSize: [160, 48],
    iconAnchor: [12, 12],
  });
}

// ============ PROPAGATION ============
function propagateSatellite(tle1, tle2, date) {
  try {
    const satrec = satellite.twoline2satrec(tle1, tle2);
    const posVel = satellite.propagate(satrec, date);
    if (!posVel.position) return null;

    const gmst = satellite.gstime(date);
    const geo = satellite.eciToGeodetic(posVel.position, gmst);

    return {
      lat: satellite.degreesLat(geo.latitude),
      lon: satellite.degreesLong(geo.longitude),
      alt: geo.height, // km
      velocity: posVel.velocity ? Math.sqrt(
        posVel.velocity.x ** 2 + posVel.velocity.y ** 2 + posVel.velocity.z ** 2
      ) : 0,
    };
  } catch {
    return null;
  }
}

function computeGroundTrack(tle1, tle2, minutes = 90) {
  const points = [];
  const now = new Date();
  const step = 60000; // 1 minute steps
  for (let i = -minutes / 2; i <= minutes / 2; i += 1) {
    const t = new Date(now.getTime() + i * step);
    const pos = propagateSatellite(tle1, tle2, t);
    if (pos) points.push([pos.lat, pos.lon]);
  }
  // Split polyline at antimeridian crossings
  const segments = [];
  let current = [];
  for (let i = 0; i < points.length; i++) {
    if (i > 0 && Math.abs(points[i][1] - points[i - 1][1]) > 180) {
      if (current.length > 1) segments.push(current);
      current = [];
    }
    current.push(points[i]);
  }
  if (current.length > 1) segments.push(current);
  return segments;
}

// ============ THEATER BOUNDING BOX (for filtering) ============
const THEATER_BOUNDS = {
  ormuz: { latMin: 10, latMax: 45, lonMin: 25, lonMax: 75 },
  med: { latMin: 28, latMax: 48, lonMin: -10, lonMax: 40 },
  indopac: { latMin: -15, latMax: 50, lonMin: 60, lonMax: 180 },
};

function isOverTheater(lat, lon, theater = 'ormuz') {
  const b = THEATER_BOUNDS[theater] || THEATER_BOUNDS.ormuz;
  return lat >= b.latMin && lat <= b.latMax && lon >= b.lonMin && lon <= b.lonMax;
}

// ============ MAIN COMPONENT ============
export default function SatelliteLayer({ satellites, theater = 'ormuz', showTracks = true }) {
  const [positions, setPositions] = useState({});
  const [selectedSat, setSelectedSat] = useState(null);
  const intervalRef = useRef(null);

  // Propagate all satellite positions every 2 seconds
  const updatePositions = useCallback(() => {
    if (!satellites?.length) return;
    const now = new Date();
    const newPos = {};
    satellites.forEach(sat => {
      const pos = propagateSatellite(sat.tle_line1, sat.tle_line2, now);
      if (pos) {
        newPos[sat.norad_id] = pos;
      }
    });
    setPositions(newPos);
  }, [satellites]);

  useEffect(() => {
    updatePositions();
    intervalRef.current = setInterval(updatePositions, 2000);
    return () => clearInterval(intervalRef.current);
  }, [updatePositions]);

  // Filter satellites visible over the theater
  const visibleSats = useMemo(() => {
    if (!satellites) return [];
    return satellites.filter(sat => {
      const pos = positions[sat.norad_id];
      if (!pos) return false;
      return isOverTheater(pos.lat, pos.lon, theater);
    });
  }, [satellites, positions, theater]);

  // Compute ground track for selected satellite
  const selectedTrack = useMemo(() => {
    if (!selectedSat) return null;
    const sat = satellites?.find(s => s.norad_id === selectedSat);
    if (!sat) return null;
    return computeGroundTrack(sat.tle_line1, sat.tle_line2, 90);
  }, [selectedSat, satellites]);

  if (!satellites?.length) return null;

  return (
    <>
      {/* Ground tracks for all visible satellites (faint) */}
      {showTracks && visibleSats.map(sat => {
        const pos = positions[sat.norad_id];
        if (!pos) return null;
        const color = TYPE_COLORS[sat.type] || '#ffffff';
        // Simple short track (past 15 min + future 15 min)
        const shortTrack = computeGroundTrack(sat.tle_line1, sat.tle_line2, 30);
        return shortTrack.map((seg, i) => (
          <Polyline
            key={`track-${sat.norad_id}-${i}`}
            positions={seg}
            pathOptions={{
              color: color,
              weight: 1,
              opacity: 0.25,
              dashArray: '4 6',
            }}
          />
        ));
      })}

      {/* Selected satellite full orbit track */}
      {selectedTrack && selectedTrack.map((seg, i) => (
        <Polyline
          key={`sel-track-${i}`}
          positions={seg}
          pathOptions={{
            color: '#00ffff',
            weight: 2,
            opacity: 0.6,
            dashArray: '6 4',
          }}
        />
      ))}

      {/* Satellite markers */}
      {visibleSats.map(sat => {
        const pos = positions[sat.norad_id];
        if (!pos) return null;
        const color = TYPE_COLORS[sat.type] || '#ffffff';
        const isSelected = selectedSat === sat.norad_id;

        return (
          <Marker
            key={`sat-${sat.norad_id}`}
            position={[pos.lat, pos.lon]}
            icon={createSatIcon(sat, color)}
            zIndexOffset={1500}
            eventHandlers={{
              click: () => setSelectedSat(isSelected ? null : sat.norad_id),
            }}
          >
            <Popup>
              <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#000', minWidth: 220 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  {sat.flag && (
                    <img src={getFlagUrl(sat.flag)} style={{ width: 24, height: 16, borderRadius: 2 }} alt="" />
                  )}
                  <strong style={{ fontSize: 14, color: color }}>{sat.name}</strong>
                </div>
                <div style={{ color: '#666', marginBottom: 4 }}>{sat.country} | {sat.type}</div>
                <table style={{ fontSize: 11, borderCollapse: 'collapse' }}>
                  <tbody>
                    <tr><td style={{ color: '#888', paddingRight: 8 }}>NORAD</td><td><strong>{sat.norad_id}</strong></td></tr>
                    <tr><td style={{ color: '#888' }}>Altitude</td><td>{pos.alt.toFixed(0)} km</td></tr>
                    <tr><td style={{ color: '#888' }}>Vitesse</td><td>{(pos.velocity * 60).toFixed(0)} km/min</td></tr>
                    <tr><td style={{ color: '#888' }}>Position</td><td>{pos.lat.toFixed(2)}N, {pos.lon.toFixed(2)}E</td></tr>
                    {sat.inclination && <tr><td style={{ color: '#888' }}>Inclinaison</td><td>{sat.inclination.toFixed(1)} deg</td></tr>}
                    {sat.period && <tr><td style={{ color: '#888' }}>Periode</td><td>{sat.period.toFixed(1)} min</td></tr>}
                  </tbody>
                </table>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </>
  );
}

// ============ CSS for satellite markers ============
export const SATELLITE_CSS = `
  .sat-marker { background: none !important; border: none !important; }
  .sat-icon {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    filter: drop-shadow(0 0 6px var(--sat-color));
  }
  .sat-pulse {
    position: absolute;
    width: 20px; height: 20px;
    border-radius: 50%;
    border: 1.5px solid;
    animation: satPulse 2s ease-out infinite;
    pointer-events: none;
  }
  .sat-label {
    position: absolute;
    top: 18px;
    left: 0;
    white-space: nowrap;
    font-family: monospace;
    font-size: 10px;
    font-weight: bold;
    background: rgba(0,0,0,0.8);
    padding: 1px 5px;
    border-radius: 2px;
    pointer-events: none;
  }
  @keyframes satPulse {
    0% { transform: scale(1); opacity: 0.6; }
    100% { transform: scale(3); opacity: 0; }
  }
`;
